import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Skeleton, Stack, Typography } from '@mui/material';
import Card from '../common/Card';

const FIELD_LABELS = {
  allowed_setups: 'Allowed setups',
  blocked_setups: 'Blocked setups',
  preferred_sl_atr: 'Preferred SL ATR',
  preferred_tp_style: 'Preferred TP style',
  best_sessions: 'Best sessions',
  min_confidence: 'Min confidence',
};

const formatValue = (value) => Array.isArray(value) ? value.join(', ') || 'N/A' : (value ?? 'N/A');

function getArrayDiff(activeValue = [], proposedValue = []) {
  const activeSet = new Set(activeValue || []);
  const proposedSet = new Set(proposedValue || []);

  const added = [...proposedSet].filter((item) => !activeSet.has(item));
  const removed = [...activeSet].filter((item) => !proposedSet.has(item));

  return { added, removed };
}

const ProfileDiffCard = ({ activeProfile, proposedProfile, changedFields, changedFieldsCount, loading = false }) => {
  const fields = Object.keys(FIELD_LABELS);

  return (
    <Card title="Profile Diff" subheader={`Changed fields: ${changedFieldsCount}`}>
      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
        </Stack>
      ) : !activeProfile || !proposedProfile ? (
        <Typography variant="body2" color="text.secondary">No proposed profile to compare yet.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {fields.map((field) => {
            const changed = changedFields.includes(field);
            const arrayDiff = Array.isArray(activeProfile[field]) && Array.isArray(proposedProfile[field])
              ? getArrayDiff(activeProfile[field], proposedProfile[field])
              : { added: [], removed: [] };
            const isAddRemove = arrayDiff.added.length || arrayDiff.removed.length;

            return (
              <Stack
                key={field}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: !changed
                    ? 'background.default'
                    : isAddRemove
                      ? 'info.dark'
                      : 'warning.dark',
                }}
              >
                <Stack sx={{ minWidth: 180 }}>
                  <Typography variant="subtitle2">{FIELD_LABELS[field]}</Typography>
                  {changed ? (
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      <Chip label="Changed" size="small" color="warning" sx={{ width: 'fit-content' }} />
                      {arrayDiff.added.length ? <Chip label={`+${arrayDiff.added.length}`} size="small" color="success" /> : null}
                      {arrayDiff.removed.length ? <Chip label={`-${arrayDiff.removed.length}`} size="small" color="error" /> : null}
                    </Stack>
                  ) : null}
                </Stack>
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Active</Typography>
                  <Typography variant="body2">{formatValue(activeProfile[field])}</Typography>
                </Stack>
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Proposed</Typography>
                  <Typography variant="body2">{formatValue(proposedProfile[field])}</Typography>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Card>
  );
};

ProfileDiffCard.propTypes = {
  activeProfile: PropTypes.object,
  proposedProfile: PropTypes.object,
  changedFields: PropTypes.arrayOf(PropTypes.string),
  changedFieldsCount: PropTypes.number,
  loading: PropTypes.bool,
};

export default ProfileDiffCard;
